<!--
  Confirmation dialog for the Uber flow.

  Same shape as the corporate portal's Confirm.vue - a coloured header band with
  a circular icon, the question in a raised panel, Cancel / Yes - so the two
  portals ask the same question the same way.

  A separate component rather than a restyle of common/Confirm.vue: that one is
  used across the whole standard flow (To be Assigned, driver assignment, admin),
  and changing it would move every one of those dialogs.

  The API is identical to common/Confirm.vue, so a call site only swaps which
  component it registers:

      const ok = await this.$refs.confirm.open(title, message, {
        icon: 'fas fa-redo',      // FontAwesome class
        color: '#000000',         // header band
        buttonName: 'Re-book',    // defaults to 'Yes'
      });
-->
<template>
  <v-dialog v-model="dialogView" persistent max-width="420" @keydown.esc="cancel">
    <v-card class="uc-card">
      <div class="uc-header" :style="{ background: options.color }">
        <div class="uc-icon">
          <i :class="options.icon"></i>
        </div>
        <h3 class="uc-title">{{ title }}</h3>
      </div>

      <div class="uc-message-wrap" v-if="message">
        <div class="uc-message">{{ message }}</div>
      </div>

      <v-card-actions class="uc-actions">
        <v-btn class="uc-btn uc-btn--cancel" depressed @click="cancel">Cancel</v-btn>
        <v-btn
          class="uc-btn uc-btn--yes"
          depressed
          :style="{ background: options.color }"
          @click="agree"
          @keydown.enter="agree"
        >
          {{ options.buttonName }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const DEFAULTS = {
  color: "#000000",
  icon: "fas fa-exclamation-circle",
  buttonName: "Yes",
};

export default {
  name: "UberConfirm",
  data() {
    return {
      dialogView: false,
      title: null,
      message: null,
      resolve: null,
      options: { ...DEFAULTS },
    };
  },
  methods: {
    open(title, message, options) {
      this.title = title;
      this.message = message;
      // Rebuilt from the defaults each time, so options left out of one call
      // are not inherited from the previous one.
      this.options = { ...DEFAULTS, ...(options || {}) };
      this.dialogView = true;

      return new Promise((resolve) => {
        this.resolve = resolve;
      });
    },
    agree() {
      this.resolve(true);
      this.dialogView = false;
    },
    cancel() {
      this.resolve(false);
      this.dialogView = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.uc-card {
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
}

.uc-header {
  padding: 28px 20px 20px;
  text-align: center;
}

.uc-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 10px;
  border-radius: 50%;
  // Lightens whatever the header colour is, so one rule covers every variant.
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 26px;
    color: #ffffff;
  }
}

.uc-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
}

.uc-message-wrap {
  margin: 16px 20px 8px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f8fafc, #eef2ff);
  border: 1px solid #e2e8f0;
}

.uc-message {
  padding: 16px 18px;
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.7;
  color: #333333;
}

.uc-actions {
  display: flex;
  justify-content: center;
  gap: 14px;
  padding: 0 20px 26px !important;
}

.uc-btn {
  min-width: 110px;
  height: 42px !important;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  border-radius: 10px;
}

.uc-btn--cancel {
  background: #f1f5f9 !important;
  color: #475569 !important;
}

.uc-btn--yes {
  color: #ffffff !important;
}
</style>
